import type { TestMapping, TestMappingInput, TestMappingTarget } from '../../../services/testMapping';

/** Select value for "create a new parameter" (never a parameter id). */
export const NEW_PARAMETER = '__new__';

/**
 * Not-yet-saved choice of one test: target id ('' = do not import,
 * NEW_PARAMETER = create one with customName / customUnit) and the factor as typed.
 */
export interface MappingDraft {
  parameterId: string;
  scale: string;
  customName?: string;
  customUnit?: string;
}

export type MappingDrafts = Readonly<Record<string, MappingDraft>>;

/** "1000", "0,001", "2.8" → number in (0, 1 000 000]; null otherwise. */
export function parseScale(input: string): number | null {
  const value = Number(input.trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 && value <= 1_000_000 ? value : null;
}

/** The draft of a row, or what is stored / suggested for it. */
export function draftOf(row: TestMapping, drafts: MappingDrafts): MappingDraft {
  return drafts[row.sourceKey] ?? { parameterId: row.parameterId ?? '', scale: String(row.scaleFactor) };
}

/** The inputs to send for every row; null while a factor is invalid or a new parameter has no name. */
export function mappingInputs(rows: readonly TestMapping[], drafts: MappingDrafts): TestMappingInput[] | null {
  const inputs: TestMappingInput[] = [];
  for (const row of rows) {
    const draft = draftOf(row, drafts);
    const scaleFactor = parseScale(draft.scale);
    if (scaleFactor === null) return null;
    const base = { sourceKey: row.sourceKey, sourceName: row.sourceName, reportedUnit: row.reportedUnit, scaleFactor };
    if (draft.parameterId === NEW_PARAMETER) {
      const name = (draft.customName ?? '').trim();
      if (name === '') return null;
      inputs.push({ ...base, parameterId: null, customParameter: { name, unit: (draft.customUnit ?? '').trim() } });
    } else {
      inputs.push({ ...base, parameterId: draft.parameterId === '' ? null : draft.parameterId });
    }
  }
  return inputs;
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/** Translated parameter name with unit: hand-test names, then ICP names, then the backend's name. */
export function targetLabel(t: TFunction, target: TestMappingTarget): string {
  const icpName = String(t(`icp.parameterNames.${target.parameterId}`, { defaultValue: target.parameterName }));
  const name = String(t(`measurement.history.parameterNames.${target.parameterId}`, { defaultValue: icpName }));
  return target.unit ? `${name} (${target.unit})` : name;
}

/**
 * Where a draft sends the test: a known target, the parameter being created
 * (once it has a name), or undefined (not imported / name missing).
 */
export function draftTarget(draft: MappingDraft, byId: ReadonlyMap<string, TestMappingTarget>): TestMappingTarget | undefined {
  if (draft.parameterId === NEW_PARAMETER) {
    const name = (draft.customName ?? '').trim();
    return name ? { parameterId: NEW_PARAMETER, parameterName: name, unit: (draft.customUnit ?? '').trim() } : undefined;
  }
  return draft.parameterId ? byId.get(draft.parameterId) : undefined;
}

export function formatValue(value: number, unit: string, language: string): string {
  return `${value.toLocaleString(language, { maximumFractionDigits: 6 })} ${unit}`.trim();
}

export function formatDate(iso: string, language: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
}
